/**
 * Proposals: the review bench for AI-drafted config changes.
 *
 * A proposal arrives from a cluster on the Corrections page, already argued
 * for: the failure mode, the evidence diffs, and one concrete rule change.
 * The reviewer's verbs are the whole interface — edit the text, approve
 * (which APPLIES it as a live bot-rule version, no deploy), or reject with a
 * note. Nothing here auto-ships and the model cannot press any of the
 * buttons.
 *
 * A proposal with no eval run is marked unmeasured, loudly. Approving one is
 * allowed — some rules are obviously right — but the reviewer does it knowing
 * they are trusting judgement, not measurement.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  FlaskConical,
  Lightbulb,
  TriangleAlert,
  XCircle,
} from 'lucide-react';
import { ApiError, apiGet, apiPatch, apiPost } from '../api/client';
import { Badge, Button, Card, CardHeader, Table, Td, Th } from '../components/ui/primitives';
import { cn } from '../lib/cn';
import { formatRelative } from '../lib/format';
import { intentLabel } from '../lib/inbox/rows';
import { diffWords } from '../lib/ticket/diff';

interface ProposalRow {
  id: string;
  title: string;
  state: 'draft' | 'approved' | 'rejected' | 'shipped';
  brand: string | null;
  clusterKey: string;
  failureMode: string | null;
  intent: string | null;
  evidenceCount: number;
  action: 'amend' | 'add';
  ruleSlug: string;
  evalRunId: string | null;
  decidedBy: string | null;
  decidedAt: string | null;
  createdAt: string;
}

interface BandSummary {
  passed: number;
  total: number;
  low: number;
  high: number;
  split: number;
}

interface Measurement {
  measured: boolean;
  reason?: string;
  repeats?: number;
  cases?: Array<{
    slug: string;
    baseline: { passed: boolean; split: boolean };
    candidate: { passed: boolean; split: boolean };
    verdict: 'improved' | 'regressed' | 'unchanged' | 'noisy';
  }>;
  counts?: { improved: number; regressed: number; unchanged: number; noisy: number };
  baseline?: BandSummary;
  candidate?: BandSummary;
}

interface ProposalDetail {
  id: string;
  title: string;
  state: ProposalRow['state'];
  brand: { name: string; slug: string } | null;
  evidence: {
    failureMode: string;
    intent: string | null;
    count: number;
    examples: Array<{ draft: string; final: string; ticketRef: string }>;
  };
  change: { action: 'amend' | 'add'; ruleSlug: string; title: string; rule: string; reason: string };
  currentRule: { title: string; rule: string } | null;
  measurement: Measurement | null;
  evalRunId: string | null;
  decidedBy: string | null;
  decidedAt: string | null;
}

const STATE_TONE: Record<ProposalRow['state'], 'held' | 'fact' | 'stop' | 'neutral'> = {
  draft: 'held',
  shipped: 'fact',
  approved: 'fact',
  rejected: 'stop',
};

const STATES: Array<{ key: string; label: string }> = [
  { key: 'draft', label: 'awaiting review' },
  { key: 'shipped', label: 'shipped' },
  { key: 'rejected', label: 'rejected' },
];

export function ProposalsPage() {
  const [state, setState] = useState<string | null>('draft');

  const proposals = useQuery({
    queryKey: ['proposals', state],
    queryFn: () => apiGet<ProposalRow[]>(`/api/proposals${state ? `?state=${state}` : ''}`),
  });

  if (proposals.isLoading) {
    return <p className="px-5 py-12 text-center text-sm text-ink-400">Loading…</p>;
  }

  const rows = proposals.data ?? [];

  return (
    <div className="mx-auto max-w-[1200px] px-5 py-5">
      <Card>
        <CardHeader title="Proposals" meta={rows.length ? `${rows.length} shown` : undefined} />

        <div className="flex flex-wrap items-center gap-1.5 border-b border-line px-4 py-2.5">
          <span className="text-xs text-ink-500">State</span>
          {STATES.map((option) => {
            const selected = state === option.key;
            return (
              <button
                key={option.key}
                type="button"
                aria-pressed={selected}
                onClick={() => setState(selected ? null : option.key)}
                className={cn(
                  'rounded-sm px-2 py-0.5 text-2xs font-semibold transition-colors duration-fast',
                  selected
                    ? 'bg-accent-50 text-accent-700 shadow-hairline'
                    : 'bg-surface-sunken text-ink-500 shadow-hairline hover:text-ink-900',
                )}
              >
                {option.label}
              </button>
            );
          })}
        </div>

        {rows.length === 0 ? (
          <div className="px-4 py-12 text-center">
            <Lightbulb size={28} strokeWidth={1.5} className="mx-auto mb-3 text-ink-300" aria-hidden />
            <p className="text-sm text-ink-700">
              {state ? `Nothing ${STATES.find((s) => s.key === state)?.label ?? state}.` : 'No proposals yet.'}
            </p>
            <p className="mx-auto mt-2 max-w-[540px] text-sm text-ink-400">
              A proposal starts on the <Link to="/corrections" className="text-accent-700 hover:underline">Corrections page</Link>:
              open a cluster and press &ldquo;Propose a fix&rdquo;. The drafter turns the evidence
              into one concrete rule change for you to review here.
            </p>
          </div>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th aria-label="Expand" className="w-8" />
                <Th>Proposal</Th>
                <Th>Brand</Th>
                <Th>Intent</Th>
                <Th className="text-right">Evidence</Th>
                <Th>State</Th>
                <Th>When</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <ProposalRowView key={row.id} row={row} />
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}

function ProposalRowView({ row }: { row: ProposalRow }) {
  const [open, setOpen] = useState(false);
  const Chevron = open ? ChevronDown : ChevronRight;

  return (
    <>
      <tr
        onClick={() => setOpen((current) => !current)}
        className="cursor-pointer transition-colors duration-fast hover:bg-surface-sunken"
      >
        <Td className="w-8 pr-0">
          <button
            type="button"
            aria-expanded={open}
            aria-label={open ? 'Collapse' : 'Review'}
            className="text-ink-400"
            onClick={(event) => {
              event.stopPropagation();
              setOpen((current) => !current);
            }}
          >
            <Chevron size={14} strokeWidth={1.75} aria-hidden />
          </button>
        </Td>
        <Td>
          <div className="text-sm text-ink-900">{row.title}</div>
          <div className="text-2xs text-ink-400">
            {row.action === 'amend' ? 'amends' : 'adds'}{' '}
            <span className="font-mono">{row.ruleSlug}</span>
            {row.failureMode ? ` · ${row.failureMode.replace(/_/g, ' ')}` : null}
          </div>
        </Td>
        <Td className="text-xs text-ink-500">{row.brand ?? '—'}</Td>
        <Td className="text-xs text-ink-500">{intentLabel(row.intent)}</Td>
        <Td className="tabular text-right text-ink-700">{row.evidenceCount}</Td>
        <Td>
          <Badge tone={STATE_TONE[row.state]}>{row.state}</Badge>
          {row.decidedBy ? <div className="text-2xs text-ink-400">{row.decidedBy}</div> : null}
        </Td>
        <Td className="text-xs text-ink-500">{formatRelative(row.createdAt)}</Td>
      </tr>
      {open ? (
        <tr>
          <Td colSpan={7} className="bg-surface-sunken">
            <ProposalReview id={row.id} />
          </Td>
        </tr>
      ) : null}
    </>
  );
}

const VERDICT_TONE: Record<string, 'fact' | 'stop' | 'neutral' | 'held'> = {
  improved: 'fact',
  regressed: 'stop',
  unchanged: 'neutral',
  noisy: 'held',
};

/** `2/3 passed (band 1–2)` — counts with the band, never a bare percentage. */
function bandLine(band: BandSummary): string {
  const base = `${band.passed}/${band.total} passed`;
  return band.split > 0 ? `${base} (band ${band.low}–${band.high})` : base;
}

function MeasurementPanel({
  measurement,
  isDraft,
}: {
  measurement: Measurement | null;
  isDraft: boolean;
}) {
  if (!measurement) {
    return isDraft ? (
      <div className="mt-4 flex items-start gap-2.5 rounded border border-line bg-hold-100 px-3 py-2.5">
        <TriangleAlert size={15} strokeWidth={1.75} className="mt-px shrink-0 text-hold-700" aria-hidden />
        <p className="text-xs text-hold-700">
          <span className="font-semibold">Unmeasured.</span> Press Measure to run the targeted eval
          cases with and without this change — three repeats each, so a flip can be told from
          noise. Approving without it is trusting judgement, not measurement.
        </p>
      </div>
    ) : null;
  }

  if (!measurement.measured) {
    return (
      <div className="mt-4 flex items-start gap-2.5 rounded border border-line bg-hold-100 px-3 py-2.5">
        <TriangleAlert size={15} strokeWidth={1.75} className="mt-px shrink-0 text-hold-700" aria-hidden />
        <p className="text-xs text-hold-700">
          <span className="font-semibold">Unmeasurable.</span> {measurement.reason}
        </p>
      </div>
    );
  }

  const counts = measurement.counts;
  return (
    <div className="mt-4 rounded border border-line bg-surface px-3 py-2.5">
      <p className="eyebrow mb-1.5 text-ink-500">
        Measured — {measurement.repeats} repeats per case, each side
      </p>

      {counts && counts.regressed > 0 ? (
        <p className="mb-2 text-xs font-semibold text-stop-700">
          {counts.regressed} case(s) regressed. That is the sentence to read first.
        </p>
      ) : null}

      <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-ink-700">
        <span>
          <span className="text-ink-400">in force:</span>{' '}
          {measurement.baseline ? bandLine(measurement.baseline) : '—'}
        </span>
        <span>
          <span className="text-ink-400">with the change:</span>{' '}
          {measurement.candidate ? bandLine(measurement.candidate) : '—'}
        </span>
      </div>

      {counts ? (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {(['regressed', 'noisy', 'improved', 'unchanged'] as const).map((verdict) =>
            counts[verdict] > 0 ? (
              <Badge key={verdict} tone={VERDICT_TONE[verdict]}>
                {counts[verdict]} {verdict}
              </Badge>
            ) : null,
          )}
        </div>
      ) : null}

      {measurement.cases && measurement.cases.length > 0 ? (
        <ul className="mt-2.5 space-y-1 border-t border-line pt-2">
          {measurement.cases.map((row) => (
            <li key={row.slug} className="flex items-center gap-2 text-xs">
              <Badge tone={VERDICT_TONE[row.verdict]}>{row.verdict}</Badge>
              <span className="font-mono text-ink-700">{row.slug}</span>
              <span className="text-ink-400">
                {row.baseline.passed ? 'pass' : 'fail'}
                {row.baseline.split ? '?' : ''} → {row.candidate.passed ? 'pass' : 'fail'}
                {row.candidate.split ? '?' : ''}
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      <p className="mt-2 text-2xs text-ink-400">
        A &ldquo;?&rdquo; marks a verdict whose own repeats disagreed. A noisy case has no verdict
        to give the change.
      </p>
    </div>
  );
}

function ProposalReview({ id }: { id: string }) {
  const queryClient = useQueryClient();
  const detail = useQuery({
    queryKey: ['proposals', 'detail', id],
    queryFn: () => apiGet<ProposalDetail>(`/api/proposals/${id}`),
  });

  const [editing, setEditing] = useState(false);
  const [ruleText, setRuleText] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [outcome, setOutcome] = useState<string | null>(null);

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['proposals'] });
  };

  const save = useMutation({
    mutationFn: (rule: string) => apiPatch<{ id: string }>(`/api/proposals/${id}`, { rule }),
    onSuccess: async () => {
      setEditing(false);
      await refresh();
    },
    onError: (error: unknown) =>
      setOutcome(error instanceof ApiError ? error.message : 'The edit was not saved.'),
  });

  const approve = useMutation({
    mutationFn: () =>
      apiPost<{ applied: { slug: string; version: number } }>(`/api/proposals/${id}/approve`),
    onSuccess: async (result) => {
      setOutcome(
        `Applied: ${result.applied.slug} v${result.applied.version} is live on the next ticket.`,
      );
      await refresh();
    },
    onError: (error: unknown) =>
      setOutcome(error instanceof ApiError ? error.message : 'The approval failed.'),
  });

  const reject = useMutation({
    mutationFn: () => apiPost<{ id: string }>(`/api/proposals/${id}/reject`, { note }),
    onSuccess: refresh,
    onError: (error: unknown) =>
      setOutcome(error instanceof ApiError ? error.message : 'The rejection failed.'),
  });

  const measure = useMutation({
    mutationFn: () => apiPost<Measurement>(`/api/proposals/${id}/evaluate`),
    onSuccess: refresh,
    // A slice run is minutes; a proxy can drop the response while the server
    // finishes. The measurement still lands on the proposal — say so instead
    // of reporting failure. (Same lesson the ticket replay learnt.)
    onError: () =>
      setOutcome(
        'The measurement is taking a while — it keeps running on the server. Reopen this proposal in a couple of minutes.',
      ),
  });

  if (detail.isLoading || !detail.data) {
    return <p className="py-4 text-center text-xs text-ink-400">Loading…</p>;
  }

  const proposal = detail.data;
  const isDraft = proposal.state === 'draft';
  const rule = ruleText ?? proposal.change.rule;
  const diffBase = proposal.currentRule?.rule ?? '';

  return (
    <div className="max-w-[820px] py-2">
      {/* The drafter's own case for the change. */}
      <p className="text-xs text-ink-500">
        <span className="font-semibold text-ink-700">Why:</span> {proposal.change.reason}
      </p>

      {/* The change itself — as a diff against the in-force text for an
          amend, whole for an add. */}
      <div className="mt-3">
        <p className="eyebrow mb-1.5 text-ink-500">
          {proposal.change.action === 'amend'
            ? `Amends ${proposal.change.ruleSlug}`
            : `New rule ${proposal.change.ruleSlug}`}
        </p>
        {editing ? (
          <>
            <textarea
              value={rule}
              onChange={(event) => setRuleText(event.target.value)}
              rows={5}
              aria-label="Proposed rule text"
              className="w-full rounded border border-line bg-surface px-3 py-2 text-sm text-ink-900"
            />
            <div className="mt-2 flex gap-2">
              <Button size="sm" variant="primary" disabled={save.isPending} onClick={() => save.mutate(rule)}>
                Save
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setRuleText(null); }}>
                Cancel
              </Button>
            </div>
          </>
        ) : (
          <div className="whitespace-pre-wrap rounded border border-line bg-surface px-3 py-2.5 text-sm leading-relaxed">
            {proposal.change.action === 'amend' && diffBase ? (
              diffWords(diffBase, rule).map((token, index) => (
                <span
                  key={index}
                  className={cn(
                    token.kind === 'del' && 'bg-stop-100 text-stop-700 line-through',
                    token.kind === 'add' && 'bg-ok-100 text-ok-700',
                  )}
                >
                  {token.text}
                </span>
              ))
            ) : (
              <span className="text-ok-700">{rule}</span>
            )}
          </div>
        )}
      </div>

      {/* Evidence: the human edits this rule claims to prevent. */}
      <div className="mt-4">
        <p className="eyebrow mb-1.5 text-ink-500">
          Evidence — {proposal.evidence.count} correction(s), {proposal.evidence.failureMode.replace(/_/g, ' ')}
        </p>
        <ul className="space-y-2.5">
          {proposal.evidence.examples.slice(0, 3).map((example, index) => (
            <li key={index} className="rounded border border-line bg-surface px-3 py-2.5">
              <p className="mb-1 font-mono text-2xs text-ink-400">#{example.ticketRef}</p>
              <p className="whitespace-pre-wrap text-sm leading-relaxed">
                {diffWords(example.draft, example.final).map((token, tokenIndex) => (
                  <span
                    key={tokenIndex}
                    className={cn(
                      token.kind === 'del' && 'bg-stop-100 text-stop-700 line-through',
                      token.kind === 'add' && 'bg-ok-100 text-ok-700',
                    )}
                  >
                    {token.text}
                  </span>
                ))}
              </p>
            </li>
          ))}
        </ul>
      </div>

      <MeasurementPanel measurement={proposal.measurement} isDraft={isDraft} />

      {outcome ? <p className="mt-3 text-xs text-ink-700">{outcome}</p> : null}

      {isDraft ? (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button
            variant="primary"
            size="sm"
            disabled={approve.isPending || editing}
            onClick={() => approve.mutate()}
          >
            <CheckCircle2 size={13} strokeWidth={1.75} aria-hidden />
            {approve.isPending ? 'Applying…' : 'Approve & apply'}
          </Button>
          <Button
            size="sm"
            disabled={measure.isPending || editing}
            onClick={() => measure.mutate()}
          >
            <FlaskConical size={13} strokeWidth={1.75} aria-hidden />
            {measure.isPending ? 'Measuring…' : proposal.measurement?.measured ? 'Measure again' : 'Measure'}
          </Button>
          <Button size="sm" disabled={editing} onClick={() => setEditing(true)}>
            Edit the rule
          </Button>
          <div className="ml-2 flex items-center gap-2">
            <input
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="Why not? Kept with the rejection."
              aria-label="Rejection note"
              className="w-64 rounded border border-line bg-surface px-2 py-1 text-xs"
            />
            <Button variant="danger" size="sm" disabled={reject.isPending} onClick={() => reject.mutate()}>
              <XCircle size={13} strokeWidth={1.75} aria-hidden />
              Reject
            </Button>
          </div>
        </div>
      ) : (
        <p className="mt-3 text-2xs text-ink-400">
          {proposal.state === 'shipped' ? 'Applied' : 'Decided'}
          {proposal.decidedBy ? ` by ${proposal.decidedBy}` : ''}
          {proposal.decidedAt ? ` ${formatRelative(proposal.decidedAt)}` : ''}.
        </p>
      )}
    </div>
  );
}
